import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { OnboardingData } from '@zero/shared/util-models';
import { environment } from 'environments/environment';
import { BehaviorSubject, catchError, map, Observable, of } from 'rxjs';
import { ClientConfigSetDefinition } from '../../entities/client-config-set-definition.model';

@Injectable({
  providedIn: 'root',
})
export class OnboardingClientConfigService {
  private _config = new BehaviorSubject<ClientConfigSetDefinition>(null);
  public get config$(): Observable<ClientConfigSetDefinition> {
    return this._config.asObservable();
  }

  private _configLoadErr = new BehaviorSubject<string>(null);
  public get configLoadErr$(): Observable<string> {
    return this._configLoadErr.asObservable();
  }

  constructor(
    private _http: HttpClient,
    private sanitizer: DomSanitizer,
  ) {}

  public triggerLoadClientConfig(regData: OnboardingData) {
    // this.reset();
    this._configLoadErr.next(null);
    this._http
      .post<ClientConfigSetDefinition>(`${environment.apihost}/api/clientconfig/v2`, regData)
      .pipe(
        catchError(() => {
          this._configLoadErr.next('Fehler beim Laden der Informationen.');
          this._config.next(null);
          return of(null);
        }),
      )
      .subscribe((ret) => {
        if (ret) {
          this._configLoadErr.next(null);
        }
        this._config.next(this.sanitize(ret));
      });
  }

  public triggerLoadClientConfigForKryptoRiskAdvice() {
    this.triggerLoadConfig('/api/clientconfig/kryptoRiskAdvice');
  }

  public triggerLoadClientConfigForKryptoCustomerOnboarding() {
    this.triggerLoadConfig('/api/clientconfig/kryptoOnboarding/v2');
  }

  public triggerLoadClientConfigForKryptoCustomerOnboardingFinalPage() {
    this.triggerLoadConfig('/api/clientconfig/kryptoOnboarding/finalpage');
  }

  public triggerLoadClientConfigForContractExcerpts() {
    this.triggerLoadConfig('/api/clientconfig/contractExcepts');
  }

  public triggerLoadClientConfigForSavingsplanCommonDocuments() {
    this.triggerLoadConfig('/api/clientconfig/savingsplan/common');
  }

  private triggerLoadConfig(apiPath: string): void {
    this._configLoadErr.next(null);
    this._http
      .get<ClientConfigSetDefinition>(`${environment.apihost + apiPath}`)
      .pipe(
        catchError(() => {
          this._configLoadErr.next('Fehler beim Laden der Informationen.');
          this._config.next(null);
          return of(null);
        }),
      )
      .subscribe((ret) => {
        if (ret) {
          this._configLoadErr.next(null);
        }
        this._config.next(this.sanitize(ret));
      });
  }

  public reset() {
    this._config.next(null);
    this._configLoadErr.next(null);
  }

  private sanitize(ccsd: ClientConfigSetDefinition): ClientConfigSetDefinition {
    if (!ccsd) {
      return null;
    }
    ccsd.docCategories?.forEach((cat) => {
      cat.headline = cat.headline ? this.sanitizer.bypassSecurityTrustHtml(cat.headline as string) : undefined;
      cat.subline = cat.subline ? this.sanitizer.bypassSecurityTrustHtml(cat.subline as string) : undefined;
      cat.docs?.forEach((d) => {
        d.title = d.title ? this.sanitizer.bypassSecurityTrustHtml(d.title as string) : undefined;
        d.subtitle = d.subtitle ? this.sanitizer.bypassSecurityTrustHtml(d.subtitle as string) : undefined;
      });
      return cat;
    });
    ccsd.contractExcerpt1 = ccsd.contractExcerpt1 ? this.sanitizer.bypassSecurityTrustHtml(ccsd.contractExcerpt1 as string) : undefined;
    ccsd.contractExcerpt2 = ccsd.contractExcerpt2 ? this.sanitizer.bypassSecurityTrustHtml(ccsd.contractExcerpt2 as string) : undefined;
    return ccsd;
  }

  public loadContractExcerpts(): Observable<ClientConfigSetDefinition> {
    return this._http
      .get<ClientConfigSetDefinition>(`${environment.apihost}/api/clientconfig/contractExcepts`)
      .pipe(map((ret) => this.sanitize(ret)));
  }
}
